<script setup lang="ts">
import { Loader2, Save } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import Button from '@/components/ui/button/Button.vue';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import {
    createOrganizationPrompt,
    getOrganizationPrompt,
    updateOrganizationPrompt
} from '@/services/organization_prompts';
import { useTenantStore } from '@/stores/tenant';
import { OrganizationPrompt } from '@/types';

const toast = useToast();
const tenantStore = useTenantStore();

const isLoading = ref(false);
const isSaving = ref(false);

const prompt = ref('');
const loadedPrompt = ref('');
const promptExists = ref(false);

const latestPromptRequestId = ref(0);

const canSave = computed(() => {
    return (
        !!tenantStore.tenantId &&
        !isLoading.value &&
        !isSaving.value &&
        prompt.value.trim() !== loadedPrompt.value.trim()
    );
});

async function loadPrompt() {
    const id = tenantStore.tenantId;
    if (!id) {
        loadedPrompt.value = '';
        prompt.value = '';
        promptExists.value = false;
        return;
    }

    const requestId = ++latestPromptRequestId.value;
    isLoading.value = true;
    try {
        const existing = await getOrganizationPrompt(id);
        const isStaleRequest =
            requestId !== latestPromptRequestId.value ||
            id !== tenantStore.tenantId;
        if (isStaleRequest) return;
        loadedPrompt.value = existing?.content ?? '';
        prompt.value = loadedPrompt.value;
        promptExists.value = existing !== null;
    } catch (error) {
        if (requestId !== latestPromptRequestId.value) return;
        console.error(error);
        toast.toast({
            title: 'Failed to load organization prompt',
            description: 'Please try again later',
            variant: 'destructive'
        });
    } finally {
        if (requestId === latestPromptRequestId.value) {
            isLoading.value = false;
        }
    }
}

async function savePrompt() {
    const id = tenantStore.tenantId;
    if (!id || isSaving.value) return;
    const requestId = ++latestPromptRequestId.value;
    isSaving.value = true;
    try {
        const content = prompt.value.trim();
        prompt.value = content;

        let saved: OrganizationPrompt;
        const promptExistedAtStart = promptExists.value;
        if (promptExistedAtStart) {
            saved = await updateOrganizationPrompt(id, content);
        } else {
            saved = await createOrganizationPrompt(id, content);
        }

        const isStaleRequest =
            requestId !== latestPromptRequestId.value ||
            id !== tenantStore.tenantId;
        if (isStaleRequest) return;

        loadedPrompt.value = saved?.content ?? content;
        prompt.value = loadedPrompt.value;
        promptExists.value = true;
        toast.toast({
            title: 'Saved',
            description: 'Organization prompt updated successfully.'
        });
    } catch (err: unknown) {
        const message = err instanceof Error ? err.message : undefined;
        toast.toast({
            title: 'Save failed',
            description:
                message ??
                'An unexpected error occurred while saving the organization prompt.',
            variant: 'destructive'
        });
    } finally {
        if (requestId === latestPromptRequestId.value) {
            isSaving.value = false;
        }
    }
}

watch(
    () => tenantStore.tenantId,
    (tenantId) => {
        // Invalidate in-flight prompt requests before resetting local state.
        latestPromptRequestId.value += 1;
        loadedPrompt.value = '';
        prompt.value = '';
        promptExists.value = false;
        isLoading.value = false;
        isSaving.value = false;
        if (!tenantId) return;
        loadPrompt();
    },
    { immediate: true }
);
</script>

<template>
    <div class="title flex flex-col gap-6">
        <div class="flex justify-between gap-4">
            <div class="flex flex-col gap-2">
                <h2 class="text-xl font-semibold">
                    Your Organization's Prompt
                </h2>
                <p class="text-sm text-muted-foreground mt-1">
                    These instructions are automatically included in every
                    request across your organization.
                </p>
            </div>

            <Button :disabled="!canSave" class="gap-2" @click="savePrompt">
                <Loader2 v-if="isSaving" class="h-4 w-4 mr-1 animate-spin" />
                <Save v-else class="h-4 w-4" />
                Save
            </Button>
        </div>

        <div class="space-y-2">
            <div
                v-if="isLoading"
                class="w-full py-14 flex items-center justify-center text-muted-foreground"
            >
                <Loader2 class="animate-spin h-5 w-5 mr-2" />
            </div>
            <div v-else>
                <Textarea
                    v-model="prompt"
                    placeholder="Enter your organization prompt..."
                    class="min-h-80 resize-y"
                    :disabled="isLoading || isSaving"
                />
            </div>
        </div>
    </div>
</template>
